/**
 * Runtime environment detection for dual-mode builds (cloud vs desktop)
 *
 * This module provides environment detection to enable:
 * - Cloud build: Next.js with PlanetScale, Inngest, Better Stack (existing web app)
 * - Desktop build: Tauri with PGlite, local jobs, offline-first (new desktop app)
 *
 * The same codebase compiles to both modes with minimal changes.
 */

import { env } from '@/env';

export type BuildMode = 'cloud' | 'desktop';

const buildMode: BuildMode = env.NEXT_PUBLIC_BUILD_MODE;
const isDesktopMode = buildMode === 'desktop';

export const runtime = {
  // Detect build mode from environment variable
  mode: buildMode,

  // Convenience flags for conditional logic
  isDesktop: isDesktopMode,
  isCloud: !isDesktopMode,

  // Feature flags based on runtime mode
  features: {
    // Background jobs: Inngest (cloud) vs in-process queue (desktop)
    inngestJobs: !isDesktopMode,

    // OAuth: Standard flow (cloud) vs hybrid device code (desktop)
    cloudAuth: !isDesktopMode,

    // Telemetry: Better Stack (cloud) vs local logs (desktop)
    remoteTelemetry: !isDesktopMode,

    // PostGIS spatial queries (cloud only, desktop uses Turf.js)
    spatialQueries: !isDesktopMode,

    // External services available in cloud mode
    turbopufferVectorSearch: !isDesktopMode,

    // Twitter API: OAuth (cloud) vs GraphQL + cookies (desktop)
    twitterGraphQL: isDesktopMode,
  },
} as const;

/**
 * Type-safe mode checking
 */
export function isDesktopBuild(): boolean {
  return runtime.mode === 'desktop';
}

export function isCloudBuild(): boolean {
  return runtime.mode === 'cloud';
}

/**
 * Assert runtime mode (throws if wrong mode)
 */
export function assertCloudMode(
  context: typeof runtime = runtime
): asserts context is typeof runtime & { mode: 'cloud' } {
  if (context.mode !== 'cloud') {
    throw new Error('This feature is only available in cloud mode');
  }
}

export function assertDesktopMode(
  context: typeof runtime = runtime
): asserts context is typeof runtime & { mode: 'desktop' } {
  if (context.mode !== 'desktop') {
    throw new Error('This feature is only available in desktop mode');
  }
}
